import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import serviceManagementService from '../../services/serviceManagementService';
import { advancedBillingService } from '../../services/advancedBillingService';
import ServiceTable from './components/ServiceTable';
import ServiceModal from './components/ServiceModal';
import CountryAvailabilityModal from './components/CountryAvailabilityModal';
import ServiceFilters from './components/ServiceFilters';
import ServiceAnalytics from './components/ServiceAnalytics';

export function ServicesManagementContent() {
  const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('language') || 'FR');
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    isActive: true,
  });

  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [servicesData, countriesData, analyticsData] = await Promise.all([
        serviceManagementService?.getAllServices(filters),
        advancedBillingService?.getAllCountries(),
        serviceManagementService?.getServiceAnalytics(),
      ]);
      setServices(servicesData);
      setCountries(countriesData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
      setServices([]);
      setCountries([]);
      setAnalytics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async (serviceData) => {
    try {
      await serviceManagementService?.createService(serviceData);
      await fetchData();
      setShowServiceModal(false);
      setSelectedService(null);
    } catch (err) {
      console.error('Error creating service:', err);
      alert('Erreur lors de la création du service: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleUpdateService = async (id, serviceData) => {
    try {
      await serviceManagementService?.updateService(id, serviceData);
      await fetchData();
      setShowServiceModal(false);
      setSelectedService(null);
    } catch (err) {
      console.error('Error updating service:', err);
      alert('Erreur lors de la mise à jour du service: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) return;
    try {
      await serviceManagementService?.deleteService(id);
      await fetchData();
    } catch (err) {
      console.error('Error deleting service:', err);
      alert('Erreur lors de la suppression du service: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setShowServiceModal(true);
  };

  const handleManageAvailability = (service) => {
    setSelectedService(service);
    setShowAvailabilityModal(true);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await serviceManagementService?.updateService(id, { isActive: !currentStatus });
      await fetchData();
    } catch (err) {
      console.error('Error toggling service status:', err);
      alert('Erreur lors de la modification du statut: ' + (err?.message || 'Erreur inconnue'));
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Gestion des services</h2>
          <p className="text-muted-foreground">
            Services orthodontiques et disponibilité par pays
          </p>
        </div>
        <Button
          onClick={() => {
            setSelectedService(null);
            setShowServiceModal(true);
          }}
          iconName="Plus"
          iconPosition="left"
        >
          Nouveau service
        </Button>
      </div>

      <ServiceAnalytics analytics={analytics} loading={loading} />

      <ServiceFilters filters={filters} onFiltersChange={setFilters} language={currentLanguage} />

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
          <p className="text-destructive font-medium">{error}</p>
          <Button onClick={fetchData} variant="outline" className="mt-4">
            Réessayer
          </Button>
        </div>
      ) : services?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Briefcase" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Aucun service trouvé</h3>
          <p className="text-muted-foreground mb-4">Commencez par créer votre premier service</p>
          <Button
            onClick={() => {
              setSelectedService(null);
              setShowServiceModal(true);
            }}
            iconName="Plus"
            iconPosition="left"
          >
            Créer un service
          </Button>
        </div>
      ) : (
        <ServiceTable
          services={services}
          countries={countries}
          onEdit={handleEditService}
          onDelete={handleDeleteService}
          onManageAvailability={handleManageAvailability}
          onToggleStatus={handleToggleStatus}
          language={currentLanguage}
        />
      )}

      {showServiceModal && (
        <ServiceModal
          service={selectedService}
          onClose={() => {
            setShowServiceModal(false);
            setSelectedService(null);
          }}
          onSave={selectedService ? handleUpdateService : handleCreateService}
          language={currentLanguage}
        />
      )}

      {showAvailabilityModal && selectedService && (
        <CountryAvailabilityModal
          service={selectedService}
          countries={countries}
          onClose={() => {
            setShowAvailabilityModal(false);
            setSelectedService(null);
          }}
          onSave={fetchData}
          language={currentLanguage}
        />
      )}
    </>
  );
}
